import { publicGet, authGet, authPost, authPatch, authDelete, newIdempotencyKey } from './http';

// Wire types for the merch API (mirrors services/api internal/shop).
//
// Track 06 (MRC-01) collapsed two merch stacks into one. The old /orders
// endpoints and their hardcoded Go catalog are gone; everything lives under
// /shop/* on uuid-keyed rows, so an order id here is the same id the admin
// console and the warehouse see.

export interface ShopCategory {
  id: string;
  league: string;
  name: string;
  slug: string;
  description?: string;
  image_blob_id?: string;
  position: number;
  active: boolean;
}

/** Presentation the server carries in `metadata`, so the store can be edited
 *  without shipping a new build. */
export interface ProductMetadata {
  kind?: string;
  subtitle?: string;
  featured?: boolean;
  colors?: { key: string; label: string; swatch: string }[];
  sizes?: string[];
}

export interface ApiProduct {
  id: string;
  league: string;
  category_id: string;
  name: string;
  description?: string;
  sku: string;
  price_cents: number;
  currency: string;
  stock_quantity: number; // -1 = unlimited
  image_blob_id?: string;
  metadata?: ProductMetadata;
  active: boolean;
  /** Sale-aware: what the fan actually pays right now. */
  effective_price_cents: number;
  on_sale: boolean;
  in_stock: boolean;
  sale_price_cents?: number;
  sale_starts_at?: string;
  sale_ends_at?: string;
  is_personalised: boolean;
}

export interface ProductVariant {
  id: string;
  size?: string;
  color?: string;
  sku: string;
  stock_quantity: number;
  price_delta_cents: number;
  effective_price_cents: number;
  in_stock: boolean;
}

export interface ProductImage {
  id: string;
  blob_id: string;
  position: number;
  alt_text?: string;
}

export interface ProductDetail extends ApiProduct {
  images: ProductImage[];
  variants: ProductVariant[];
}

export interface PickupLocation {
  id: string;
  league: string;
  name: string;
  address: string;
  hours?: string;
  active: boolean;
}

export interface CartItem {
  id: string;
  product_id: string;
  variant_id?: string;
  quantity: number;
  price_cents: number;
  size?: string;
  color?: string;
  added_at: string;
  name?: string;
  image_blob_id?: string;
}

export interface Cart {
  id: string;
  items: CartItem[];
  subtotal_cents: number;
  member_discount_cents: number;
  member_discount_pct: number;
  total_cents: number;
  currency: string;
  expires_at: string;
}

export type OrderStageState = 'done' | 'current' | 'pending';

export interface OrderStage {
  key: string;
  label: string;
  state: OrderStageState;
  at?: string;
}

export interface OrderItem {
  id: string;
  product_id: string;
  variant_id?: string;
  name: string;
  quantity: number;
  price_cents: number;
  size?: string;
  color?: string;
}

export type OrderStatus =
  | 'pending'
  | 'paid'
  | 'preparing'
  | 'dispatched'
  | 'delivered'
  | 'picked_up'
  | 'returned'
  | 'refunded'
  | 'cancelled'
  | 'failed';

export type ReturnStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'shipped_back'
  | 'received'
  | 'refunded'
  | 'exchanged';

export type ReasonCode =
  | 'defective'
  | 'wrong_item'
  | 'wrong_size'
  | 'not_as_described'
  | 'changed_mind';

export interface ShopReturn {
  id: string;
  order_id: string;
  order_item_id?: string;
  reason_code: ReasonCode;
  reason?: string;
  photo_blob_id?: string;
  status: ReturnStatus;
  resolution?: 'refund' | 'exchange';
  refund_cents?: number;
  exchange_order_id?: string;
  cs_note?: string;
  requested_at: string;
  reviewed_at?: string;
  refunded_at?: string;
}

/** Why the "Return or exchange" control is (or isn't) live. */
export interface Returnability {
  eligible: boolean;
  reason?: 'not_delivered' | 'window_expired' | 'personalised' | 'already_open';
  deadline?: string;
}

export interface ShopOrder {
  id: string;
  order_number: string;
  currency: string;
  status: OrderStatus;
  payment_method?: string;
  payment_ref?: string;
  // OMT's hosted checkout page, while the order is still unpaid and payable.
  payment_url?: string;
  subtotal_cents: number;
  shipping_cents: number;
  discount_cents: number;
  discount_reason?: string;
  /** The promo code applied, and what it took off. promo_discount_cents is a
   *  component of discount_cents, not a further deduction (issue #101). */
  promo_code?: string;
  promo_discount_cents: number;
  total_cents: number;
  fulfillment_method: 'ship' | 'pickup';
  shipping_method?: 'standard' | 'express';
  recipient_name?: string;
  recipient_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
  pickup_location_id?: string;
  pickup_location?: PickupLocation;
  tracking_number?: string;
  carrier?: string;
  created_at: string;
  paid_at?: string;
  dispatched_at?: string;
  delivered_at?: string;
  pickup_ready_at?: string;
  picked_up_at?: string;
  estimated_delivery?: string;
  items: OrderItem[];
  tracking?: OrderStage[];
  return_status?: ReturnStatus;
  return?: ShopReturn;
  returnable?: Returnability;
  /** The carrier's view of the parcel when it was shipped through one: the
   *  waybill and the carrier's scans, newest first. */
  shipment?: OrderShipment;
}

export interface ShipmentEvent {
  code: string;
  /** The carrier's own words for the scan ("Out for delivery"). */
  description: string;
  location?: string;
  at: string;
}

export interface OrderShipment {
  carrier: string;
  waybill: string;
  status: 'created' | 'delivered' | 'returned';
  events: ShipmentEvent[];
}

// --- catalog (public) ---

export async function listCategories(league: string): Promise<ShopCategory[]> {
  const res = await publicGet<{ categories: ShopCategory[] }>(
    `/shop/categories?league=${encodeURIComponent(league)}`,
  );
  return res.categories ?? [];
}

export async function listCategoryProducts(categoryId: string): Promise<ApiProduct[]> {
  const res = await publicGet<{ products: ApiProduct[] }>(`/shop/categories/${categoryId}/products`);
  return res.products ?? [];
}

/** The catalog's orderings (the API's `sort`). */
export type ProductSort = 'newest' | 'price_asc' | 'price_desc' | 'name';

export interface ProductQuery {
  league: string;
  categoryId?: string;
  q?: string;
  /** Bounds on the price the fan pays (the sale price while one is live), in cents. */
  minPriceCents?: number;
  maxPriceCents?: number;
  inStock?: boolean;
  onSale?: boolean;
  sort?: ProductSort;
  limit?: number;
  offset?: number;
}

export interface ProductPage {
  products: ApiProduct[];
  /** Products matching the FILTER, not the page — what a pager is sized from. */
  total: number;
  limit: number;
  offset: number;
  /** Cheapest and dearest price matching every filter EXCEPT the price bounds,
   *  so a price filter can always be widened again. Null when nothing matches. */
  price_range?: { min_cents: number; max_cents: number } | null;
}

/**
 * One page of the store (GET /shop/products).
 *
 * Category and search are applied in SQL. This used to return the whole
 * catalog, which the store screen rendered in a single grid and the
 * all-products screen then filtered in JavaScript.
 */
export async function listProducts(params: ProductQuery): Promise<ProductPage> {
  const qs = new URLSearchParams({ league: params.league });
  if (params.categoryId) qs.set('category_id', params.categoryId);
  if (params.q) qs.set('q', params.q);
  if (params.minPriceCents !== undefined) qs.set('min_price', String(params.minPriceCents));
  if (params.maxPriceCents !== undefined) qs.set('max_price', String(params.maxPriceCents));
  if (params.inStock) qs.set('in_stock', 'true');
  if (params.onSale) qs.set('on_sale', 'true');
  if (params.sort && params.sort !== 'newest') qs.set('sort', params.sort);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const res = await publicGet<ProductPage>(`/shop/products?${qs}`);
  return { ...res, products: res.products ?? [] };
}

export function getProduct(productId: string): Promise<ProductDetail> {
  return publicGet<ProductDetail>(`/shop/products/${productId}`);
}

export async function listPickupLocations(league: string): Promise<PickupLocation[]> {
  const res = await publicGet<{ pickup_locations: PickupLocation[] }>(
    `/shop/pickup-locations?league=${encodeURIComponent(league)}`,
  );
  return res.pickup_locations ?? [];
}

// --- cart (authed) ---

/** GET, not POST. The route has always been a GET; the client used to send a
 *  POST here, so every cart read 405'd. */
export function getCart(token: string): Promise<Cart> {
  return authGet<Cart>('/shop/cart', token);
}

export interface AddToCartRequest {
  product_id: string;
  variant_id?: string;
  quantity: number;
  size?: string;
  color?: string;
}

/** Cart mutations return the whole updated cart, so the badge and totals
 *  redraw without a follow-up read. */
export function addToCart(token: string, request: AddToCartRequest): Promise<Cart> {
  return authPost<Cart>('/shop/cart/items', token, request);
}

export function removeFromCart(token: string, itemId: string): Promise<Cart> {
  return authDelete<Cart>(`/shop/cart/items/${itemId}`, token);
}

// --- checkout ---

export interface AddressInput {
  recipient_name: string;
  recipient_phone: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  region?: string;
  postal_code?: string;
  country?: string;
}

export interface CheckoutRequest {
  payment_method: 'wallet' | 'payment_intent';
  fulfillment_method?: 'ship' | 'pickup';
  shipping_method?: 'standard' | 'express';
  address?: AddressInput;
  pickup_location_id?: string;
  notes?: string;
  /** A promo code the fan applied (issue #101). An unusable code fails the
   *  checkout rather than being ignored — being charged full price after typing
   *  a code, with no word said, is a silent overcharge. */
  promo_code?: string;
}

export function checkout(token: string, request: CheckoutRequest): Promise<ShopOrder> {
  return authPost<ShopOrder>('/shop/checkout', token, request, {
    idempotencyKey: newIdempotencyKey(),
  });
}

// --- promo codes (issue #101) ---

export interface AppliedPromo {
  promo_code_id: string;
  code: string;
  name: string;
  discount_type: 'percent' | 'amount';
  discount_value: number;
  discount_cents: number;
  /** The part of the basket the code applied to — less than the subtotal when
   *  the code is scoped to some categories. */
  eligible_cents: number;
}

export interface CartQuote {
  subtotal_cents: number;
  bundle_saving_cents: number;
  member_discount_cents: number;
  promo_discount_cents: number;
  discount_cents: number;
  total_goods_cents: number;
  currency: string;
  promo?: AppliedPromo;
}

/** What a code would do to the basket, without committing to anything.
 *
 *  POST rather than GET because the code travels in a body: promo codes in a
 *  query string end up in access logs, history and referrer headers. The server
 *  re-checks everything at checkout, so a quote is an estimate, not a promise. */
export function quoteCartPromo(token: string, code: string): Promise<CartQuote> {
  return authPost<CartQuote>('/shop/cart/promo-quote', token, { code });
}

/** What shipping will cost for the caller's cart to this address — the same
 *  figure checkout charges: Aramex's live rate when it answers, the flat fee
 *  otherwise. */
export interface ShippingQuote {
  shipping_cents: number;
  currency: string;
  source: 'carrier' | 'flat';
}

export function quoteShipping(
  token: string,
  request: Pick<CheckoutRequest, 'fulfillment_method' | 'shipping_method' | 'address'>,
): Promise<ShippingQuote> {
  return authPost<ShippingQuote>('/shop/shipping/quote', token, request);
}

// --- orders ---

export async function listMyOrders(token: string): Promise<ShopOrder[]> {
  const res = await authGet<{ orders: ShopOrder[] }>('/shop/orders', token);
  return res.orders ?? [];
}

export function getOrder(token: string, orderId: string): Promise<ShopOrder> {
  return authGet<ShopOrder>(`/shop/orders/${orderId}`, token);
}

// --- returns ---

export interface OpenReturnRequest {
  order_item_id?: string;
  reason_code: ReasonCode;
  reason?: string;
  photo_blob_id?: string;
  resolution: 'refund' | 'exchange';
}

export function openReturn(
  token: string,
  orderId: string,
  request: OpenReturnRequest,
): Promise<ShopReturn> {
  return authPost<ShopReturn>(`/shop/orders/${orderId}/returns`, token, request, {
    idempotencyKey: newIdempotencyKey(),
  });
}

export function getReturn(token: string, orderId: string): Promise<ShopReturn> {
  return authGet<ShopReturn>(`/shop/orders/${orderId}/returns`, token);
}

// --- notify me (FR-MRC-002) ---

export function notifyMe(
  token: string,
  productId: string,
  variantId?: string,
): Promise<{ watching: boolean }> {
  return authPost<{ watching: boolean }>(`/shop/products/${productId}/notify-me`, token, {
    variant_id: variantId,
  });
}

export function cancelNotifyMe(token: string, productId: string, variantId?: string): Promise<void> {
  const qs = variantId ? `?variant_id=${encodeURIComponent(variantId)}` : '';
  return authDelete<void>(`/shop/products/${productId}/notify-me${qs}`, token);
}

export function isWatching(
  token: string,
  productId: string,
  variantId?: string,
): Promise<{ watching: boolean }> {
  const qs = variantId ? `?variant_id=${encodeURIComponent(variantId)}` : '';
  return authGet<{ watching: boolean }>(`/shop/products/${productId}/notify-me${qs}`, token);
}

/** Admin restock — used by dev tooling to exercise the back-in-stock fan-out. */
export function adjustStock(
  token: string,
  productId: string,
  body: { variant_id?: string; delta?: number; absolute?: number },
) {
  return authPatch(`/admin/shop/products/${productId}/stock`, token, body);
}
